import {InfiniteData, Query, QueryClient, QueryKey} from '@tanstack/react-query';
import produce, {castDraft} from 'immer';

import {tasksStore} from 'IocContainer';
import {QUERY_CACHE_KEYS} from 'shared/constants/queryCache';
import {TaskDetailsModelDTO} from 'shared/models/task/task';
import {CompanyWorker} from 'shared/models/worker';

// eslint-disable-next-line
type ItemWithId = {id?: any};
export const CACHE_ACTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

type UpdateDeleteParams = Pick<TaskDetailsModelDTO, 'id' | 'dateList'> &
  Partial<Omit<TaskDetailsModelDTO, 'id' | 'dateList'>>;
export type DailiesSyncParams =
  | {task: UpdateDeleteParams; action: Omit<Action, 'create'>}
  | {task: TaskDetailsModelDTO; action: Omit<Action, 'update' | 'delete'>};

type Action = (typeof CACHE_ACTIONS)[keyof typeof CACHE_ACTIONS];
export type DailiesSyncTask = Pick<TaskDetailsModelDTO, 'id' | 'dateList'> &
  Partial<Omit<TaskDetailsModelDTO, 'id' | 'dateList'>>;

export class QueryCacheHelper {
  private static instance: QueryCacheHelper;
  private queryClient: QueryClient | null = null;

  static getInstance(): QueryCacheHelper {
    if (!QueryCacheHelper.instance) {
      QueryCacheHelper.instance = new QueryCacheHelper();
    }
    return QueryCacheHelper.instance;
  }

  initialize(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  getQueryClient(): QueryClient {
    if (!this.queryClient) {
      throw new Error('QueryCacheHelper not initialized');
    }
    return this.queryClient;
  }

  findRecentPagedQuery<D extends Record<string, unknown>>(key: QueryKey) {
    if (!key) return null;
    return this.queryClient
      .getQueryCache()
      .findAll({
        queryKey: key,
        exact: false,
      })
      .sort((a, b) => (a.state.dataUpdatedAt < b.state.dataUpdatedAt ? 1 : -1))[0] as Query<InfiniteData<D[]>, Error>;
  }

  findActivePagedQuery<D extends Record<string, unknown>>(key: QueryKey) {
    if (!key) return null;
    return this.queryClient.getQueryCache().find<InfiniteData<D[]>, Error>({
      queryKey: key,
      exact: false,
      type: 'active',
    });
  }

  updatePagedData<D extends ItemWithId>(state: InfiniteData<D[]>, newItem: D, replace?: boolean) {
    return state?.pages
      ? produce(state, (draft: InfiniteData<D[]>) => {
          for (const page of draft.pages) {
            this.updateDraftArrayItem(page, newItem, replace);
          }
        })
      : state;
  }

  prependToPagedData<D extends Record<string, unknown>>(state: InfiniteData<D[]>, newItem: D) {
    return state?.pages
      ? produce(state, (draft: InfiniteData<D[]>) => {
          if (draft.pages[0]) {
            draft.pages[0] = [newItem, ...draft.pages[0]];
          }
        })
      : null;
  }

  findInPagedData<D extends Record<string, unknown>>(state: InfiniteData<D[]>, predicate: (item: D) => boolean) {
    if (!state?.pages) return null;
    for (const page of state.pages) {
      const element = page.find(predicate);
      if (element) return element;
    }
    return null;
  }

  removeItemFromPagedData<D extends Record<string, unknown>>(state: InfiniteData<D[]>, itemToRemove: D | string) {
    const id = typeof itemToRemove === 'string' ? itemToRemove : itemToRemove.id;
    return state?.pages
      ? produce(state, (draft: InfiniteData<D[]>) => {
          draft.pages = draft.pages.map((page) => page.filter((pageItem) => pageItem.id !== id));
        })
      : state;
  }

  updateItem<D extends ItemWithId>(key: QueryKey, updatedItem: D) {
    if (!key) return null;
    const query = this.queryClient.getQueryCache().find<D[], Error>({
      queryKey: key,
    });

    if (query) {
      this.queryClient.setQueryData(key, (oldData: D[]) =>
        produce(oldData, (draft: D[]) => this.updateDraftArrayItem(draft, updatedItem)),
      );
    }
    return query;
  }

  removeItem<D extends ItemWithId>(key: QueryKey, itemToDelete: D | string) {
    if (!key) return null;
    const query = this.queryClient.getQueryCache().find<D[], Error>({
      queryKey: key,
    });

    if (query) {
      this.queryClient.setQueryData(key, (oldData: D[]) =>
        produce(oldData, (draft) => {
          const index = draft.findIndex((item) => {
            const targetId =
              typeof itemToDelete === 'string' || typeof itemToDelete === 'number' ? itemToDelete : itemToDelete.id;
            return targetId === item.id;
          });
          if (index !== -1) {
            draft.splice(index, 1);
          }
        }),
      );
    }
    return query;
  }

  prependItem<D extends Record<keyof D, unknown>>(key: QueryKey, newItem: D) {
    const query = this.queryClient.getQueryCache().find<D[], Error>({
      queryKey: key,
    });

    if (query) {
      this.queryClient.setQueryData(key, (oldData: D[]) =>
        produce(oldData, (draft) => {
          draft.unshift(castDraft(newItem));
        }),
      );
    }
  }

  draftPrepend<D extends ItemWithId>(draft: InfiniteData<D[]>, _state: unknown, newWorker: D) {
    if (draft.pages[0]) {
      draft.pages[0] = [newWorker, ...draft.pages[0]];
      return draft.pages[0];
    }
    return null;
  }

  clearWorkerNumber<D extends CompanyWorker>(draft: InfiniteData<D[]>, _state: unknown, newWorker: D) {
    if (draft.pages[0]) {
      return (draft.pages[0] = draft.pages[0].map<D>((worker) => {
        if (worker.workerFull.mobileNumber === newWorker.workerFull.mobileNumber) {
          return newWorker;
        }
        return worker;
      }));
    }
    return null;
  }

  prependOrUpdateCompanyWorkers<D extends CompanyWorker>(state: InfiniteData<D[]>, newWorker: D) {
    return state?.pages
      ? produce(state, (draft: InfiniteData<D[]>) => {
          if (!this.hasWorker(state.pages[0], newWorker)) {
            this.draftPrepend(draft, state, newWorker);
          }
          if (this.hasRemovedWorker(state.pages[0], newWorker)) {
            this.clearWorkerNumber(draft, state, newWorker);
          }
        })
      : null;
  }

  private updateDraftArrayItem<D extends ItemWithId>(data: D[], updatedItem: D, replace?: boolean) {
    const index = data.findIndex((pageItem) => pageItem.id === updatedItem.id);
    if (index !== -1) {
      data.splice(index, 1, replace ? updatedItem : Object.assign({}, data[index], updatedItem));
    }
  }

  private hasWorker<D extends CompanyWorker>(workers: D[], newWorker: D) {
    return workers.some((worker) => worker.id === newWorker.id);
  }

  private hasRemovedWorker<D extends CompanyWorker>(workers: D[], newWorker: D) {
    return workers.some((worker) => worker.id === newWorker.id && worker.status === 'removed');
  }

  invalidateIssueForDailies() {
    const projectId = tasksStore.projectId;
    this.queryClient.invalidateQueries({queryKey: [QUERY_CACHE_KEYS.dailiesIssues, projectId], type: 'all'});
  }
}

export const queryCacheHelper = QueryCacheHelper.getInstance();
